import { apiClient } from './api'
import type {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  RefreshTokenRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  User,
  TwoFactorSetupResponse,
  TwoFactorVerifyRequest,
  RecoveryCodesResponse,
  TrustedDevice,
  TwoFactorMethod
} from '@/types/auth'

import { getOriginHostForEmailCta as getOriginHostname } from '@/utils/originHost'

export class AuthService {
  // Login user
  static async login(credentials: LoginRequest): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/auth/login', credentials)
  }

  // Register new user
  static async register(userData: RegisterRequest): Promise<AuthResponse> {
    // Use extended timeout for registration as it may include email sending
    return apiClient.post<AuthResponse>('/auth/register', {
      ...userData,
      originHostname: getOriginHostname(),
    }, {
      timeout: 120000 // 2 minutes timeout for registration
    })
  }

  // External OAuth login (Google, Apple, Facebook)
  static async externalLogin(data: {
    provider: 'Google' | 'Apple' | 'Facebook'
    idToken: string
    accessToken?: string
    nonce?: string
  }): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/auth/external-login', data)
  }

  // Refresh access token
  static async refreshToken(refreshTokenData: RefreshTokenRequest): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/auth/refresh', refreshTokenData)
  }

  // Logout user
  static async logout(): Promise<void> {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Logout timeout')), 5000)
    })
    
    const logoutPromise = apiClient.post<void>('/auth/logout')
    
    return Promise.race([logoutPromise, timeoutPromise])
  }

  // Revoke all user tokens
  static async revokeAllTokens(): Promise<void> {
    return apiClient.post<void>('/auth/revoke-all')
  }


  // Request password reset
  static async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    return apiClient.post<void>('/auth/forgot-password', {
      ...data,
      originHostname: getOriginHostname(),
    })
  }

  // Reset password
  static async resetPassword(data: ResetPasswordRequest): Promise<void> {
    return apiClient.post<void>('/auth/reset-password', data)
  }

  // Verify email
  static async verifyEmail(token: string): Promise<{ requiresPasswordReset: boolean; passwordResetToken?: string }> {
    return apiClient.get<{ requiresPasswordReset: boolean; passwordResetToken?: string }>(`/auth/verify-email?token=${encodeURIComponent(token)}`)
  }

  // Check if email exists in the system
  // Throws on network error so the caller can show an error to the user
  static async checkEmailExists(email: string): Promise<{ exists: boolean; error?: string }> {
    try {
      const response = await apiClient.get<{ exists: boolean }>(`/auth/check-email?email=${encodeURIComponent(email)}`)
      return { exists: response.exists }
    } catch (error) {
      console.error('Error checking email:', error)
      return { exists: false, error: 'Unable to verify email. Please try again.' }
    }
  }

  // Resend verification email
  static async resendVerificationEmail(email: string, brand?: string): Promise<void> {
    return apiClient.post<void>('/auth/resend-verification', {
      email,
      brand,
      originHostname: getOriginHostname(),
    })
  }

  // Get current user profile
  static async getCurrentUser(): Promise<User> {
    return apiClient.get<User>('/auth/me')
  }

  // Update user profile
  static async updateProfile(userData: Partial<User>): Promise<User> {
    console.log('AuthService: updateProfile called with userData:', userData)
    console.log('AuthService: Making PUT request to /auth/me')
    return apiClient.put<User>('/auth/me', userData)
  }

  // Change password
  static async changePassword(data: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
  }): Promise<void> {
    return apiClient.post<void>('/auth/change-password', data)
  }

  // Two-Factor Authentication Methods

  // Setup two-factor authentication (get QR code and secret)
  static async setupTwoFactor(method: TwoFactorMethod): Promise<TwoFactorSetupResponse> {
    return apiClient.post<TwoFactorSetupResponse>('/auth/2fa/setup', { method })
  }

  // Enable two-factor authentication (confirm with code)
  static async enableTwoFactor(data: {
    method: TwoFactorMethod
    secret: string
    code: string
    recoveryCodes: string[]
  }): Promise<void> {
    return apiClient.post<void>('/auth/2fa/enable', data)
  }

  // Disable two-factor authentication
  static async disableTwoFactor(code: string): Promise<void> {
    return apiClient.post<void>('/auth/2fa/disable', { code })
  }

  // Verify two-factor code during login
  static async verifyTwoFactor(data: TwoFactorVerifyRequest): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/auth/2fa/verify', data)
  }

  // Send email code during login (for email-based 2FA)
  static async sendEmailCodeForLogin(userId: string): Promise<void> {
    return apiClient.post<void>('/auth/2fa/send-email-code-login', userId)
  }

  // Send email code for authenticated user (for setup/verification)
  static async sendEmailCode(): Promise<void> {
    return apiClient.post<void>('/auth/2fa/send-email-code')
  }

  // Regenerate recovery codes
  static async regenerateRecoveryCodes(code: string): Promise<RecoveryCodesResponse> {
    return apiClient.post<RecoveryCodesResponse>('/auth/2fa/regenerate-recovery-codes', { code })
  }

  // Get trusted devices
  static async getTrustedDevices(): Promise<TrustedDevice[]> {
    return apiClient.get<TrustedDevice[]>('/auth/2fa/trusted-devices')
  }

  // Remove a trusted device
  static async removeTrustedDevice(deviceId: string): Promise<void> {
    return apiClient.delete<void>(`/auth/2fa/trusted-devices/${deviceId}`)
  }

  // Get user sessions
  static async getSessions(): Promise<Array<{
    id: string
    deviceName: string
    ipAddress: string
    location: string
    lastActivity: string
    isCurrent: boolean
  }>> {
    return apiClient.get<Array<{
      id: string
      deviceName: string
      ipAddress: string
      location: string
      lastActivity: string
      isCurrent: boolean
    }>>('/auth/sessions')
  }

  // Revoke specific session
  static async revokeSession(sessionId: string): Promise<void> {
    return apiClient.delete<void>(`/auth/sessions/${sessionId}`)
  }
}

export default AuthService